// The 3D view's model as files that hold no WebGL: a Wavefront OBJ with its MTL,
// and a 3D DXF. Each mesh in its record's colour (sceneNodes.ts) — a wood's or
// a tile's, never the white its texture is multiplied by. Pure: they run on a
// scene built in node.

import * as THREE from 'three';
import { rgbToAci } from '../../shared/dxf';
import {
  lookOf,
  nodeOf,
  ROLE,
} from './sceneNodes';

/** Serialise a group to a Wavefront OBJ + companion MTL (with colours), one
 *  material per distinct mesh colour. Pure — no WebGL needed. */
export function groupToObjMtl(group: THREE.Object3D): { obj: string; mtl: string } {
  group.updateMatrixWorld(true);
  const obj: string[] = ['mtllib model.mtl'];
  const mtl: string[] = [];
  const matName = new Map<string, string>();
  const v = new THREE.Vector3();
  let vBase = 0, meshN = 0;
  group.traverseVisible((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    const pos = mesh.geometry.getAttribute('position') as THREE.BufferAttribute | undefined;
    if (!pos) return;
    const mat = (Array.isArray(mesh.material) ? mesh.material[0] : mesh.material) as THREE.MeshStandardMaterial;
    const node = nodeOf(mesh) ?? lookOf(mesh), col = node.color;
    const key = col.getHexString();
    let name = matName.get(key);
    if (!name) {
      name = `m${matName.size}`;
      matName.set(key, name);
      mtl.push(`newmtl ${name}`, `Kd ${col.r.toFixed(4)} ${col.g.toFixed(4)} ${col.b.toFixed(4)}`);
      if (!ROLE[node.role].opaque && mat?.opacity < 1) mtl.push(`d ${mat.opacity.toFixed(3)}`);
    }
    obj.push(`o mesh${meshN++}`);
    for (let i = 0; i < pos.count; i++) {
      v.fromBufferAttribute(pos, i).applyMatrix4(mesh.matrixWorld);
      obj.push(`v ${v.x.toFixed(3)} ${v.y.toFixed(3)} ${v.z.toFixed(3)}`);
    }
    obj.push(`usemtl ${name}`);
    const idx = mesh.geometry.getIndex();
    if (idx) for (let i = 0; i < idx.count; i += 3) obj.push(`f ${vBase + idx.getX(i) + 1} ${vBase + idx.getX(i + 1) + 1} ${vBase + idx.getX(i + 2) + 1}`);
    else for (let i = 0; i < pos.count; i += 3) obj.push(`f ${vBase + i + 1} ${vBase + i + 2} ${vBase + i + 3}`);
    vBase += pos.count;
  });
  return { obj: obj.join('\n') + '\n', mtl: mtl.join('\n') + '\n' };
}

/** Serialise a group to a 3D DXF (AutoCAD R12) using 3DFACE entities — one per
 *  mesh triangle, one layer per distinct mesh colour. The three.js scene is Y-up
 *  with the plan on X/-Z (three = worldX, height, -worldY); DXF is Z-up, so map
 *  each vertex to (worldX, worldY, height) = (three.x, -three.z, three.y) so the
 *  export lines up with the 2D plan and extrudes upward in Z. Pure — no WebGL. */
export function groupToDxf3d(group: THREE.Object3D): string {
  group.updateMatrixWorld(true);
  const out: string[] = [];
  const pair = (code: number, value: string | number): void => { out.push(String(code)); out.push(String(value)); };
  const num = (v: number): string => {
    if (!isFinite(v)) v = 0;
    return (Math.abs(v) < 1e-9 ? 0 : v).toFixed(4).replace(/\.?0+$/, (s) => (s.includes('.') ? '' : s));
  };

  // Collect triangles grouped by layer (one layer per distinct mesh colour).
  interface Tri { a: THREE.Vector3; b: THREE.Vector3; c: THREE.Vector3; }
  const byLayer = new Map<string, { color: number; tris: Tri[] }>();
  const v = new THREE.Vector3();
  const map = (i: number, pos: THREE.BufferAttribute, m: THREE.Matrix4): THREE.Vector3 => {
    v.fromBufferAttribute(pos, i).applyMatrix4(m);
    return new THREE.Vector3(v.x, -v.z, v.y); // three (x, y=up, z) → DXF (x, y, z=up)
  };
  group.traverseVisible((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    const pos = mesh.geometry.getAttribute('position') as THREE.BufferAttribute | undefined;
    if (!pos) return;
    const col = (nodeOf(mesh) ?? lookOf(mesh)).color;
    const hex = '#' + col.getHexString();
    const layer = 'C_' + col.getHexString().toUpperCase();
    let bucket = byLayer.get(layer);
    if (!bucket) { bucket = { color: rgbToAci(hex) ?? 7, tris: [] }; byLayer.set(layer, bucket); }
    const mw = mesh.matrixWorld;
    const idx = mesh.geometry.getIndex();
    if (idx) {
      for (let i = 0; i < idx.count; i += 3)
        bucket.tris.push({ a: map(idx.getX(i), pos, mw), b: map(idx.getX(i + 1), pos, mw), c: map(idx.getX(i + 2), pos, mw) });
    } else {
      for (let i = 0; i < pos.count; i += 3)
        bucket.tris.push({ a: map(i, pos, mw), b: map(i + 1, pos, mw), c: map(i + 2, pos, mw) });
    }
  });

  // ---- HEADER ----
  pair(0, 'SECTION'); pair(2, 'HEADER');
  pair(9, '$ACADVER'); pair(1, 'AC1009');
  pair(9, '$MEASUREMENT'); pair(70, 1);
  pair(0, 'ENDSEC');

  // ---- TABLES ----
  const layers = [...byLayer.entries()];
  pair(0, 'SECTION'); pair(2, 'TABLES');
  pair(0, 'TABLE'); pair(2, 'LTYPE'); pair(70, 1);
  pair(0, 'LTYPE'); pair(2, 'CONTINUOUS'); pair(70, 0); pair(3, 'Solid line'); pair(72, 65); pair(73, 0); pair(40, 0);
  pair(0, 'ENDTAB');
  pair(0, 'TABLE'); pair(2, 'LAYER'); pair(70, layers.length + 1);
  pair(0, 'LAYER'); pair(2, '0'); pair(70, 0); pair(62, 7); pair(6, 'CONTINUOUS');
  for (const [name, b] of layers) { pair(0, 'LAYER'); pair(2, name); pair(70, 0); pair(62, b.color); pair(6, 'CONTINUOUS'); }
  pair(0, 'ENDTAB');
  pair(0, 'ENDSEC');

  pair(0, 'SECTION'); pair(2, 'BLOCKS'); pair(0, 'ENDSEC');

  // ---- ENTITIES ----
  pair(0, 'SECTION'); pair(2, 'ENTITIES');
  const vert = (base: number, p: THREE.Vector3): void => {
    pair(base, num(p.x)); pair(base + 10, num(p.y)); pair(base + 20, num(p.z));
  };
  for (const [name, b] of layers) {
    for (const t of b.tris) {
      pair(0, '3DFACE'); pair(8, name); pair(62, b.color);
      vert(10, t.a); vert(11, t.b); vert(12, t.c); vert(13, t.c); // 4th vertex repeats 3rd (triangle)
    }
  }
  pair(0, 'ENDSEC');
  pair(0, 'EOF');
  return out.join('\r\n') + '\r\n';
}
